import React, { useState } from 'react';
import {
  ShieldCheck,
  Zap,
  QrCode,
  LineChart,
  Building,
  FileSpreadsheet,
  Award,
  Layers,
  Search,
  BookOpen,
  Smartphone,
  CheckCircle2,
  Users,
  Check,
  ArrowRight
} from 'lucide-react';

interface BenefitsSectionProps {
  onOpenRegister?: () => void;
  onExploreCatalog?: () => void;
}

export const BenefitsSection: React.FC<BenefitsSectionProps> = ({
  onOpenRegister,
  onExploreCatalog,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'penulis' | 'penerbit' | 'organisasi' | 'pembaca'>('all');

  const benefitsData = [
    // Penulis
    {
      role: 'penulis',
      roleBadge: 'Penulis Mandiri & Akademisi',
      roleColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: Award,
      title: 'Perlindungan Hak Cipta & Kepemilikan Digital',
      desc: 'Setiap naskah yang terdaftar memperoleh stempel waktu digital (timestamp) permanen di pangkalan data nasional UIBN sebagai bukti sah kepemilikan dan prioritas publikasi karya intelektual.',
      points: [
        'Bukti pencatatan digital dengan User ID dan metadata resmi',
        'Mencegah plagiarisme dan klaim sepihak naskah nirkode',
        'Dapat dipublikasikan secara publik maupun berstatus naskah diajukan',
      ],
    },
    {
      role: 'penulis',
      roleBadge: 'Penulis Mandiri & Akademisi',
      roleColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: Zap,
      title: 'Akses Pendaftaran Ramah Self-Publishing',
      desc: 'Penulis independen, komunitas literasi, dan akademisi tidak lagi diwajibkan mendirikan badan hukum PT/CV hanya untuk mendapatkan identitas buku yang sah dan terverifikasi.',
      points: [
        'Formulir pendaftaran naskah mandiri yang cepat dan mudah',
        'Penetapan nomor draft dan kalkulasi checksum otomatis',
        'Peluang menghubungkan naskah ke penerbit terverifikasi di UIBN',
      ],
    },
    {
      role: 'penulis',
      roleBadge: 'Penulis Mandiri & Akademisi',
      roleColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: QrCode,
      title: 'Barcode & QR Code Resolusi Tinggi Siap Cetak',
      desc: 'Sistem langsung menghasilkan barcode 1D standar Code128 / EAN13 dan QR Code 2D dalam format SVG vektor dan PNG berkualitas tinggi tanpa perlu biaya generator pihak ketiga.',
      points: [
        'Unduh file vektor SVG tanpa pecah untuk percetakan offset',
        'QR Code mengarah langsung ke halaman autentikasi buku',
        'Standar layout siap tempel pada sampul belakang (back cover)',
      ],
    },
    {
      role: 'penulis',
      roleBadge: 'Penulis Mandiri & Akademisi',
      roleColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: LineChart,
      title: 'Transparansi Pemindaian & Statistik Minat',
      desc: 'Dapatkan analitik real-time mengenai seberapa sering barcode buku Anda dipindai oleh pembaca, pustakawan, atau toko buku di seluruh Indonesia.',
      points: [
        'Grafik total pemindaian harian dan mingguan',
        'Log histori lokasi/metode scan (kamera vs input manual)',
        'Notifikasi real-time saat status buku diverifikasi penerbit',
      ],
    },
    {
      role: 'penulis',
      roleBadge: 'Penulis Mandiri & Akademisi',
      roleColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: Award,
      title: 'Sertifikat Elektronik Registrasi Buku Resmi',
      desc: 'Setiap karya yang disetujui di UIBN memperoleh sertifikat elektronik (E-Sertifikat) resmi berformat PDF beresolusi tinggi secara gratis yang siap diunduh kapan saja.',
      points: [
        'Dokumen PDF resmi siap cetak lengkap dengan nomor registrasi unik',
        'Sangat berguna untuk pengajuan KUM fungsional dosen/guru serta portofolio',
        'QR Code pengaman pada sertifikat untuk pembuktian keaslian instan',
      ],
    },

    // Penerbit
    {
      role: 'penerbit',
      roleBadge: 'Penerbit & Rumah Produksi',
      roleColor: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: Building,
      title: 'Alokasi Kode Penerbit Unik (Publisher Code)',
      desc: 'Penerbit terdaftar mendapatkan prefiks kode numerik 4-digit eksklusif yang disematkan pada setiap judul, memperkuat kredibilitas dan identitas institusi terbitan.',
      points: [
        'Prefiks penerbit resmi (misal: 8042, 9103) di setiap UIBN',
        'Konsistensi penomoran katalog untuk seluruh edisi dan seri',
        'Verifikasi lembaga penerbit resmi berbadan hukum',
      ],
    },
    {
      role: 'penerbit',
      roleBadge: 'Penerbit & Rumah Produksi',
      roleColor: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: Layers,
      title: 'Otomasi Produksi & Efisiensi Operasional',
      desc: 'Hilangkan biaya lisensi generator barcode terpisah dan pangkas waktu pra-cetak. Tim desain grafis dapat mengunduh aset barcode dan QR resmi dalam hitungan detik.',
      points: [
        'Paket aset cetak SVG dan PNG 300 DPI gratis',
        'Mengurangi risiko kesalahan pengetikan angka ISBN/UIBN',
        'Proses kurasi naskah masuk langsung melalui dashboard',
      ],
    },
    {
      role: 'penerbit',
      roleBadge: 'Penerbit & Rumah Produksi',
      roleColor: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: FileSpreadsheet,
      title: 'Manajemen Katalog Terpusat & Ekspor CSV',
      desc: 'Kelola puluhan hingga ribuan judul terbitan dalam satu antarmuka terpadu lengkap dengan pencarian cepat, status naskah, dan ekspor data CSV berstandar industri.',
      points: [
        'Ekspor seluruh database judul ke format CSV UTF-8 BOM',
        'Kompatibel langsung dengan Microsoft Excel dan Google Sheets',
        'Kemudahan sinkronisasi inventaris gudang dan distributor buku',
      ],
    },
    {
      role: 'penerbit',
      roleBadge: 'Penerbit & Rumah Produksi',
      roleColor: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: ShieldCheck,
      title: 'Perlindungan Brand & Anti-Pembajakan',
      desc: 'Buku terbitan memiliki segel verifikasi digital yang mempersulit oknum mencetak buku bajakan karena pembaca dapat langsung memvalidasi keaslian ke server UIBN.',
      points: [
        'Halaman publik resmi memvalidasi status keaslian cetakan',
        'Pemberitahuan peringatan jika ada kode yang terduga dipalsukan',
        'Meningkatkan kepercayaan toko buku dan rantai distribusi',
      ],
    },
    {
      role: 'penerbit',
      roleBadge: 'Penerbit & Rumah Produksi',
      roleColor: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: Award,
      title: 'Sertifikat Elektronik Registrasi Buku Penerbit',
      desc: 'Setiap nomor UIBN yang diterbitkan di bawah bendera penerbit resmi Anda dibekali dengan E-Sertifikat digital sebagai bukti sah pendaftaran katalog nasional.',
      points: [
        'E-Sertifikat resmi bertanda tangan digital dalam berkas PDF siap unduh',
        'Validasi legalitas terbitan yang aman bagi penulis mitra penerbit',
        'Memperkuat portofolio administrasi dan kredibilitas rumah produksi',
      ],
    },

    // Organisasi & Komunitas
    {
      role: 'organisasi',
      roleBadge: 'Organisasi & Komunitas Literasi',
      roleColor: 'bg-amber-50 text-amber-800 border-amber-200',
      icon: Users,
      title: 'Pendaftaran Langsung UIBN Karya Komunitas',
      desc: 'Komunitas literasi, yayasan sosial, dan forum akademik dapat langsung mendaftarkan antologi atau karya bersama ke Admin UIBN layaknya penerbit resmi setelah diverifikasi.',
      points: [
        'Pengajuan buku langsung ke Admin UIBN tanpa perantara',
        'Verifikasi profil resmi organisasi untuk menjaga integritas komunitas',
        'Aset Barcode SVG, QR Code, dan E-Sertifikat resmi untuk karya anggota',
      ],
    },
    {
      role: 'organisasi',
      roleBadge: 'Organisasi & Komunitas Literasi',
      roleColor: 'bg-amber-50 text-amber-800 border-amber-200',
      icon: ShieldCheck,
      title: 'Identitas Organisasi Terverifikasi & Terkunci',
      desc: 'Nama organisasi yang didaftarkan dikunci permanen pasca-verifikasi admin untuk menjamin keabsahan dan mencegah klaim kepemilikan sepihak atas karya literasi.',
      points: [
        'Kepastian identitas komunitas di pangkalan data nasional',
        'Katalog terpusat untuk mendokumentasikan seluruh publikasi organisasi',
        'Ekspor dan pelacakan pemindaian pembaca secara transparan',
      ],
    },

    // Pembaca & Ekosistem
    {
      role: 'pembaca',
      roleBadge: 'Pembaca, Toko Buku & Perpustakaan',
      roleColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: Smartphone,
      title: 'Verifikasi Keaslian 1-Detik Lewat Ponsel',
      desc: 'Pembaca dan pengelola toko buku cukup mengarahkan kamera ke barcode atau QR code untuk memastikan bahwa buku yang dipegang adalah edisi resmi terbitan terdaftar.',
      points: [
        'Scanner barcode & QR langsung di browser tanpa install aplikasi',
        'Tampilan instan data judul, penulis, penerbit, dan tahun',
        'Memberantas peredaran buku fotokopian dan bajakan di pasaran',
      ],
    },
    {
      role: 'pembaca',
      roleBadge: 'Pembaca, Toko Buku & Perpustakaan',
      roleColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: Search,
      title: 'Katalog Nasional Terbuka & Metadata Lengkap',
      desc: 'Mencari referensi buku berkualitas, sinopsis resmi, dan profil penulis Indonesia dengan mudah melalui mesin pencari terpadu.',
      points: [
        'Pencarian instan berdasarkan judul, penulis, penerbit, atau kode',
        'Informasi kategori DDC (Dewey Decimal Classification) terstruktur',
        'Dukungan REST API untuk integrasi sistem perpustakaan digital',
      ],
    },
  ];

  const filteredBenefits =
    activeTab === 'all'
      ? benefitsData
      : benefitsData.filter((b) => b.role === activeTab);

  return (
    <section id="section-benefits-uibn" className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 space-y-10 2xl:space-y-12">
      {/* Header */}
      <div className="text-center max-w-3xl 2xl:max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs sm:text-sm 2xl:text-base font-semibold shadow-2xs">
          <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
          <span>Keuntungan & Nilai Tambah</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Benefit Mendaftarkan Buku di UIBN
        </h2>
        <p className="text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-600 leading-relaxed">
          Dirancang untuk memberikan manfaat nyata bagi seluruh pemangku kepentingan industri perbukuan: Penulis, Penerbit, Toko Buku, dan Pembaca.
        </p>
      </div>

      {/* Role Filter Tabs */}
      <div className="flex items-center justify-center gap-2.5 flex-wrap text-xs sm:text-sm 2xl:text-base font-semibold">
        <button
          type="button"
          id="btn-filter-benefit-all"
          onClick={() => setActiveTab('all')}
          className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl transition cursor-pointer ${
            activeTab === 'all'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          Semua Manfaat ({benefitsData.length})
        </button>

        <button
          type="button"
          id="btn-filter-benefit-penulis"
          onClick={() => setActiveTab('penulis')}
          className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl transition cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'penulis'
              ? 'bg-sky-600 text-white shadow-xs'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5" />
          <span>Untuk Penulis</span>
        </button>

        <button
          type="button"
          id="btn-filter-benefit-penerbit"
          onClick={() => setActiveTab('penerbit')}
          className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl transition cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'penerbit'
              ? 'bg-purple-600 text-white shadow-xs'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Building className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5" />
          <span>Untuk Penerbit</span>
        </button>

        <button
          type="button"
          id="btn-filter-benefit-pembaca"
          onClick={() => setActiveTab('pembaca')}
          className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl transition cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'pembaca'
              ? 'bg-emerald-600 text-white shadow-xs'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5" />
          <span>Untuk Pembaca & Toko Buku</span>
        </button>
      </div>

      {/* Benefits Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 2xl:gap-8">
        {filteredBenefits.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={`benefit-card-${item.title}-${index}`}
              className="bg-white rounded-3xl p-6 sm:p-7 2xl:p-8 border border-slate-200/90 shadow-2xs hover:shadow-sm hover:border-indigo-300 transition flex flex-col justify-between space-y-4 group"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Icon className="w-5 h-5 2xl:w-6 2xl:h-6" />
                  </div>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs 2xl:text-sm font-bold uppercase tracking-wider border ${item.roleColor}`}
                  >
                    {item.roleBadge}
                  </span>
                </div>

                <h3 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 leading-relaxed">
                  {item.desc}
                </p>

                <ul className="space-y-2.5 pt-3 border-t border-slate-100 text-xs sm:text-sm lg:text-base 2xl:text-[16px] text-slate-600">
                  {item.points.map((pt, pIdx) => (
                    <li key={`${pt}-${pIdx}`} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 2xl:w-5 2xl:h-5 text-emerald-600 shrink-0 mt-0.5" />
                      <span className="leading-snug">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action banner */}
      <div className="p-6 sm:p-8 2xl:p-10 rounded-3xl bg-gradient-to-r from-indigo-50/70 via-white to-purple-50/70 border border-indigo-100/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="space-y-1 max-w-xl 2xl:max-w-2xl">
          <h4 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">
            Ingin Mendaftarkan Judul Buku Pertama Anda?
          </h4>
          <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600">
            Daftar gratis sekarang dan nikmati kemudahan pengelolaan nomor buku UIBN.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {onOpenRegister && (
            <button
              type="button"
              id="btn-benefits-register"
              onClick={onOpenRegister}
              className="px-5 sm:px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs sm:text-sm 2xl:text-base transition shadow-2xs cursor-pointer"
            >
              Registrasi Buku Sekarang
            </button>
          )}
          {onExploreCatalog && (
            <button
              type="button"
              id="btn-benefits-explore"
              onClick={onExploreCatalog}
              className="px-4 sm:px-5 py-3 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-semibold text-xs sm:text-sm 2xl:text-base border border-slate-200 transition cursor-pointer"
            >
              Cari di Katalog
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
